import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth.js";
import { currentNavigation, visibleNavigation } from "../lib/navigation.js";
import { AgentChat } from "./AgentChat.js";
import { ReportarErro } from "./ReportarErro.js";
import { NOSHOT_ATTR } from "../lib/screenshot.js";

function Navigation({ onNavigate, mobile = false }: { onNavigate?: () => void; mobile?: boolean }) {
  const { user, logout, canArea, precisaTrocarSenha } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const groups = visibleNavigation(canArea, search);

  return (
    <div className="flex h-full flex-col">
      <div className="brand-stripe h-1.5 shrink-0" />
      <div className="px-5 pb-4 pt-5">
        <Link to="/" onClick={onNavigate} aria-label="Brasil Novo — Início" className="block rounded-xl bg-brand-600 px-3 py-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600">
          <img src="/logo-grupo-brasil-novo.png" alt="Grupo Brasil Novo" width={347} height={94} className="h-auto w-full" />
        </Link>
        <p className="mt-3 text-sm text-brand-800">Controle operacional</p>
      </div>
      {!precisaTrocarSenha && (
        <div className="px-4 pb-3">
          <label htmlFor={mobile ? "menu-search-mobile" : "menu-search"} className="sr-only">Buscar página</label>
          <input id={mobile ? "menu-search-mobile" : "menu-search"} type="search" className="input" placeholder="Buscar página…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      )}
      <nav aria-label="Navegação principal" className="min-h-0 flex-1 overflow-y-auto px-3 pb-5">
        {precisaTrocarSenha ? (
          <p className="rounded-lg bg-gold-50 px-3 py-2 text-sm leading-relaxed text-gold-900">Defina sua senha para acessar o portal.</p>
        ) : groups.length === 0 ? (
          <div className="px-2 py-4 text-sm text-slate-600" role="status">Nenhuma página encontrada.
            <button className="mt-2 block font-semibold text-brand-700 underline" onClick={() => setSearch("")}>Limpar busca</button>
          </div>
        ) : groups.map((group) => (
          <section key={group.label} className="mb-4 last:mb-0">
            <h2 className="mb-1 px-3 text-xs font-semibold text-slate-500">{group.label}</h2>
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.to === "/"} onClick={onNavigate}
                  className={({ isActive }) => `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-600 ${isActive ? "bg-brand-600 text-white" : "text-slate-700 hover:bg-brand-50"}`}>
                  {item.image ? <img src={item.image} alt="" width={24} height={24} className="h-6 w-6 shrink-0 object-contain" /> : <span aria-hidden="true" className="flex h-6 w-6 shrink-0 items-center justify-center text-lg">{item.icon}</span>}
                  {item.label}
                </NavLink>
              ))}
            </div>
          </section>
        ))}
      </nav>
      <div className="shrink-0 border-t border-brand-100 px-5 py-4">
        <p className="truncate text-sm font-semibold text-slate-800">{user?.name}</p>
        <div className="mt-2 flex items-center justify-between gap-2 text-xs">
          {!precisaTrocarSenha && <Link to="/trocar-senha" onClick={onNavigate} className="text-brand-700 underline underline-offset-2">Trocar senha</Link>}
          <button className="font-semibold text-slate-600 hover:text-brand-700" onClick={async () => { await logout(); onNavigate?.(); navigate("/login"); }}>Sair</button>
        </div>
      </div>
    </div>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const { canArea, precisaTrocarSenha } = useAuth();
  const { pathname } = useLocation();
  const current = currentNavigation(pathname);
  const [agentOpen, setAgentOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef<HTMLDialogElement>(null);
  const menuButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (mobileOpen) menuRef.current?.showModal();
    else menuRef.current?.close();
  }, [mobileOpen]);
  useEffect(() => { setMobileOpen(false); }, [pathname]);
  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const closeOnDesktop = () => { if (desktop.matches) setMobileOpen(false); };
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, []);

  return (
    <div className="min-h-screen bg-brand-50/60">
      <a href="#conteudo" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[70] focus:rounded-lg focus:bg-white focus:p-3 focus:text-brand-800">Ir para o conteúdo</a>
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-brand-100 bg-white lg:block"><Navigation /></aside>
      <dialog ref={menuRef} aria-label="Menu de navegação"
        onKeyDownCapture={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            setMobileOpen(false);
          }
        }}
        onCancel={() => setMobileOpen(false)}
        onClose={() => { setMobileOpen(false); menuButton.current?.focus(); }}
        className="fixed inset-y-0 left-0 m-0 h-dvh max-h-none w-80 max-w-[90vw] border-0 bg-white p-0 text-slate-800 shadow-2xl backdrop:bg-brand-950/50">
        {mobileOpen && <div className="flex h-full flex-col">
          <div className="flex shrink-0 items-center justify-between px-5 py-3"><span className="font-semibold">Menu</span><button onClick={() => setMobileOpen(false)} className="btn-ghost" aria-label="Fechar menu">Fechar</button></div>
          <div className="min-h-0 flex-1"><Navigation mobile onNavigate={() => setMobileOpen(false)} /></div>
        </div>}
      </dialog>
      <div className="min-w-0 lg:pl-64">
        <header className="flex min-h-20 flex-wrap items-center justify-between gap-3 border-b border-brand-100 bg-white px-4 py-4 sm:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <button ref={menuButton} onClick={() => setMobileOpen(true)} aria-label="Abrir menu" aria-expanded={mobileOpen} className="btn-ghost lg:hidden">☰ <span>Menu</span></button>
            <div className="min-w-0"><p className="text-xs text-slate-500">{current.group}</p><p className="truncate text-sm font-semibold text-brand-900">{current.label}</p></div>
          </div>
          {!precisaTrocarSenha && canArea("operacoes") && (pathname === "/torre" || pathname === "/operacoes") && (
            <div className="flex flex-wrap gap-2">
              {/* Visão auxiliar no formato da planilha de programação (D-25). */}
              {pathname === "/torre" && <Link to="/torre/planilha" className="btn-ghost" data-testid="link-visao-planilha">Visão planilha</Link>}
              <Link to={pathname === "/torre" ? "/operacoes" : "/torre"} className="btn-ghost">
                {pathname === "/torre" ? "Tabela de operações" : "Ver Torre de Controle"}
              </Link>
            </div>
          )}
          {!precisaTrocarSenha && pathname === "/" && <Link to="/chamados" className="flex items-center gap-2 text-sm font-medium text-brand-800"><img src="/icons/suporte.png" alt="" width={28} height={28} className="h-7 w-7 object-contain" />Suporte</Link>}
        </header>
        <main id="conteudo" tabIndex={-1} className="mx-auto min-w-0 max-w-[1600px] overflow-x-auto px-4 pb-28 pt-6 outline-none sm:px-8 sm:pt-8">{children}</main>
      </div>
      <ReportarErro />
      {!precisaTrocarSenha && canArea("agente") && <>
        <button {...{ [NOSHOT_ATTR]: "" }}
          className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-brand-600 shadow-lg ring-1 ring-brand-700 transition hover:bg-brand-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
          onClick={() => setAgentOpen((v) => !v)} title="Agente IA" aria-label="Ajuda de IA" aria-expanded={agentOpen} aria-controls="ajuda-ia-panel">
          <img src="/icons/ajuda-ia.png" alt="" width={44} height={44} className="h-11 w-11 object-contain" />
        </button>
        {agentOpen && <section id="ajuda-ia-panel" aria-label="Ajuda de IA" {...{ [NOSHOT_ATTR]: "" }}
          className="fixed bottom-24 right-4 z-40 max-h-[calc(100dvh-7rem)] w-96 max-w-[calc(100vw-2rem)] overflow-auto rounded-xl border border-brand-100 bg-white p-4 shadow-2xl sm:right-6">
          <div className="mb-3 flex items-center justify-between gap-3">
            <span className="flex items-center gap-2 font-semibold text-slate-800"><img src="/icons/ajuda-ia.png" alt="" width={32} height={32} className="h-8 w-8 object-contain" />Agente IA</span>
            <button className="btn-ghost" aria-label="Fechar ajuda de IA" onClick={() => setAgentOpen(false)}>Fechar</button>
          </div>
          <AgentChat compact />
        </section>}
      </>}
    </div>
  );
}
